import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { VendorPerformanceRow } from '@/hooks/useVendorPerformance';
import { format } from 'date-fns';
import { JobCategory, JOB_CATEGORIES } from '@/lib/types/job';

interface VendorExportButtonProps {
  data: VendorPerformanceRow[] | undefined;
  isLoading: boolean;
  startDate: Date;
  endDate: Date;
  categories: JobCategory[];
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function escapeCSV(value: string | number): string {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatTime(hours: number): string {
  if (hours === 0) return 'N/A';
  if (hours < 48) {
    return `${Math.round(hours)} hours`;
  }
  return `${(hours / 24).toFixed(1)} days`;
}

export function VendorExportButton({
  data,
  isLoading,
  startDate,
  endDate,
  categories,
}: VendorExportButtonProps) {
  const handleExport = () => {
    if (!data || data.length === 0) return;

    const categoryLabels = categories.length === 0
      ? 'All Categories'
      : categories.map(c => JOB_CATEGORIES.find(jc => jc.value === c)?.label || c).join(', ');

    // Build CSV content
    const lines: string[] = [];

    // Header with metadata
    lines.push(`Vendor Performance Report`);
    lines.push(`Date Range: ${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`);
    lines.push(`Categories: ${categoryLabels}`);
    lines.push(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`);
    lines.push('');

    // Column headers
    lines.push([
      'Vendor Name',
      'Certifications',
      'Bids Submitted',
      'Avg Response Time',
      'Win Rate (%)',
      'Jobs Completed',
      'Total Value',
      'Avg Job Value',
    ].map(escapeCSV).join(','));

    // Data rows
    for (const vendor of data) {
      lines.push([
        escapeCSV(vendor.vendorName),
        escapeCSV(vendor.certifications.join('; ')),
        vendor.bidsSubmitted,
        escapeCSV(formatTime(vendor.avgResponseTimeHours)),
        vendor.winRate.toFixed(1),
        vendor.jobsCompleted,
        escapeCSV(formatCurrency(vendor.totalValue)),
        escapeCSV(formatCurrency(vendor.avgJobValue)),
      ].join(','));
    }

    // Summary row
    lines.push('');
    lines.push(`Total Vendors,${data.length}`);
    lines.push(`Total Bids,${data.reduce((sum, v) => sum + v.bidsSubmitted, 0)}`);
    lines.push(`Total Jobs Completed,${data.reduce((sum, v) => sum + v.jobsCompleted, 0)}`);
    lines.push(`Total Value,${escapeCSV(formatCurrency(data.reduce((sum, v) => sum + v.totalValue, 0)))}`);

    // Create and download file
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vendor-performance-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={isLoading || !data || data.length === 0}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      Export CSV
    </Button>
  );
}
